import { useEffect, useRef, useState } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import AuthLayout from './AuthLayout';
import Button from 'components/Common/Button';
import MuiLoader from 'components/Common/Loader';
import { notify } from 'components/Common/Toast';
import Routes from 'constants/routes';
import { acceptStaffInvitation, previewStaffInvitation, type StaffInvitationPreview } from 'services/staffInvitationsApi';
import { getProblemDetail } from 'api/v1Helpers';
import { appClient } from 'api/apiClient';
import { AppDispatch, RootState } from 'redux/store';
import { logoutUser, refreshMeStaffRoles, setSelectedParish } from 'redux/slices/user/userSlice';
import {
    clearStaffInviteToken,
    isAuthRequiredStaffInviteError,
    isTerminalStaffInviteError,
    isWrongAccountStaffInviteError,
    readStaffInviteEmail,
    readStaffInviteToken,
    storeStaffInviteEmail,
    storeStaffInviteToken,
} from 'utils/staffInviteToken';
import './Auth.css';

type Phase = 'loading' | 'need-auth' | 'accepting' | 'success' | 'error';

const AcceptInvite = () => {
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const accessToken = useSelector((s: RootState) => s.auth.data?.accessToken);
    const refreshToken = useSelector((s: RootState) => s.auth.data?.refreshToken);
    const userEmail = useSelector((s: RootState) => s.user.userInfo?.username || s.user.userResponse?.email);
    const [phase, setPhase] = useState<Phase>('loading');
    const [message, setMessage] = useState('');
    const [inviteEmail, setInviteEmail] = useState(() => readStaffInviteEmail() || '');
    const [preview, setPreview] = useState<StaffInvitationPreview | null>(null);
    const [canRetryWithOtherAccount, setCanRetryWithOtherAccount] = useState(false);
    const [canRetryAccept, setCanRetryAccept] = useState(false);
    const attempted = useRef(false);
    const previewAttempted = useRef(false);
    const [acceptNonce, setAcceptNonce] = useState(0);

    const tokenFromQuery = searchParams.get('token') || '';
    const token = tokenFromQuery || readStaffInviteToken() || '';

    useEffect(() => {
        if (!tokenFromQuery) return;
        storeStaffInviteToken(tokenFromQuery);
        navigate(Routes.auth.acceptInvite, { replace: true });
    }, [tokenFromQuery, navigate]);

    // Outside ProtectedRoute — push rehydrated Redux tokens onto appClient before accept.
    useEffect(() => {
        if (!accessToken) return;
        appClient.setToken(accessToken, refreshToken);
    }, [accessToken, refreshToken]);

    // Always load preview (logged in or out) so status/parish/role are known before accept.
    useEffect(() => {
        if (tokenFromQuery) return;
        if (!token || previewAttempted.current) return;
        previewAttempted.current = true;
        (async () => {
            try {
                const p = await previewStaffInvitation(token);
                storeStaffInviteEmail(p.email);
                setInviteEmail(p.email);
                setPreview(p);
                if (p.status !== 'PENDING') {
                    clearStaffInviteToken();
                    setCanRetryWithOtherAccount(false);
                    setCanRetryAccept(false);
                    setPhase('error');
                    setMessage(
                        p.status === 'EXPIRED'
                            ? 'This invitation has expired.'
                            : p.status === 'REVOKED'
                              ? 'This invitation has been revoked.'
                              : p.status === 'ACCEPTED'
                                ? 'This invitation has already been accepted.'
                                : 'This invitation cannot be accepted.'
                    );
                }
            } catch (err) {
                const detail = getProblemDetail(err) || 'This invitation link is invalid or has expired.';
                if (isTerminalStaffInviteError(err)) {
                    clearStaffInviteToken();
                } else {
                    previewAttempted.current = false;
                }
                setCanRetryWithOtherAccount(false);
                setCanRetryAccept(false);
                setPhase('error');
                setMessage(detail);
            }
        })();
    }, [token, tokenFromQuery]);

    useEffect(() => {
        if (tokenFromQuery) return; // wait for replace navigation
        if (!token) {
            setPhase((p) => (p === 'error' || p === 'success' ? p : 'error'));
            setMessage((m) => m || 'This invitation link is missing a token.');
            setCanRetryWithOtherAccount(false);
            setCanRetryAccept(false);
            return;
        }
        // Preview first — drives need-auth copy and blocks accept on non-PENDING.
        if (!preview) {
            if (!accessToken && previewAttempted.current === false) {
                // still loading preview
            } else if (!accessToken && inviteEmail) {
                setPhase('need-auth');
            }
            return;
        }
        if (preview.status !== 'PENDING') {
            return; // error phase already set by preview effect
        }
        if (!accessToken) {
            setPhase('need-auth');
            return;
        }
        if (attempted.current) return;
        attempted.current = true;
        setPhase('accepting');
        setCanRetryAccept(false);
        (async () => {
            try {
                appClient.setToken(accessToken, refreshToken);
                const result = await acceptStaffInvitation(token);
                clearStaffInviteToken();
                await dispatch(refreshMeStaffRoles());
                dispatch(
                    setSelectedParish({
                        parishId: result.parishId,
                        name: preview.parishName,
                    })
                );
                setPhase('success');
                setMessage(`You have joined ${preview.parishName || 'the parish'} as ${result.role.replace(/_/g, ' ')}.`);
                notify('Invitation accepted.', 'success');
            } catch (err) {
                if (isAuthRequiredStaffInviteError(err)) {
                    attempted.current = false;
                    setCanRetryAccept(false);
                    setCanRetryWithOtherAccount(false);
                    setPhase('need-auth');
                    return;
                }
                const detail = getProblemDetail(err) || 'Could not accept this invitation.';
                const wrongAccount = isWrongAccountStaffInviteError(err);
                const terminal = isTerminalStaffInviteError(err);
                if (terminal) {
                    clearStaffInviteToken();
                }
                setCanRetryWithOtherAccount(wrongAccount);
                setCanRetryAccept(!terminal && !wrongAccount);
                setPhase('error');
                setMessage(detail);
            }
        })();
    }, [accessToken, token, tokenFromQuery, dispatch, acceptNonce, preview, refreshToken, inviteEmail]);

    const goLogin = () =>
        navigate(Routes.auth.login, {
            state: { email: inviteEmail || readStaffInviteEmail() || '' },
        });
    const goRegister = () => {
        const email = inviteEmail || readStaffInviteEmail() || '';
        navigate(Routes.auth.register, { state: { email, lockEmail: Boolean(email) } });
    };
    const switchAccount = async () => {
        await dispatch(logoutUser({ silent: true }));
        setPhase('need-auth');
        attempted.current = false;
        setCanRetryAccept(false);
    };
    const retryAccept = () => {
        attempted.current = false;
        setCanRetryAccept(false);
        setAcceptNonce((n) => n + 1);
    };

    const inviteMeta = preview ? (
        <Stack gap={0.5} sx={{ mb: 1 }}>
            {preview.parishName ? (
                <Typography sx={{ fontSize: 14, color: 'text.secondary' }}>Parish: {preview.parishName}</Typography>
            ) : null}
            <Typography sx={{ fontSize: 14, color: 'text.secondary' }}>Role: {preview.role.replace(/_/g, ' ')}</Typography>
            {inviteEmail ? <Typography sx={{ fontSize: 14, color: 'text.secondary' }}>Invitation for: {inviteEmail}</Typography> : null}
        </Stack>
    ) : inviteEmail ? (
        <Typography sx={{ fontSize: 14, color: 'text.secondary', mb: 1 }}>Invitation for: {inviteEmail}</Typography>
    ) : null;

    return (
        <AuthLayout>
            <div className="Auth-form">
                <Box p={4}>
                    <Typography variant="h5" sx={{ mb: 2 }}>
                        Staff invitation
                    </Typography>
                    {(phase === 'loading' || phase === 'accepting') && <MuiLoader loading absolute />}
                    {phase === 'need-auth' && (
                        <Stack gap={2}>
                            <Typography>
                                Sign in with the invited email if you already have an account, or create an account with that email. You
                                will return here to accept after authentication.
                            </Typography>
                            {inviteMeta}
                            {userEmail ? (
                                <Typography sx={{ fontSize: 14, color: 'text.secondary' }}>Current session: {String(userEmail)}</Typography>
                            ) : null}
                            <Button title="Sign in" onClick={goLogin} />
                            <Button title="Create account" onClick={goRegister} variant="outlined" disabled={!inviteEmail} />
                        </Stack>
                    )}
                    {phase === 'success' && (
                        <Stack gap={2}>
                            <Typography>{message}</Typography>
                            <Button title="Go to portal" onClick={() => navigate(Routes.home.main)} />
                        </Stack>
                    )}
                    {phase === 'error' && (
                        <Stack gap={2}>
                            <Typography color="error">{message}</Typography>
                            {canRetryAccept ? <Button title="Try again" onClick={retryAccept} /> : null}
                            {canRetryWithOtherAccount ? <Button title="Try again with another account" onClick={switchAccount} /> : null}
                            <Button title="Sign in" onClick={goLogin} variant="outlined" />
                        </Stack>
                    )}
                </Box>
            </div>
        </AuthLayout>
    );
};

export default AcceptInvite;
